import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, FileText, ClipboardList, Bell, Users, TrendingUp, Video, MessageCircle, FileQuestion, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const LMSDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_content: 0,
    total_assignments: 0,
    pending_submissions: 0,
    total_announcements: 0
  });
  const [recentContent, setRecentContent] = useState([]);
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, contentRes, assignmentRes, classesRes, quizzesRes] = await Promise.all([
        api.get('/lms/stats/teacher'),
        api.get('/lms/content'),
        api.get('/lms/assignments'),
        api.get('/lms/online-classes', { params: { upcoming_only: true } }),
        api.get('/lms/quizzes')
      ]);
      
      setStats(statsRes.data || {});
      setRecentContent((contentRes.data || []).slice(0, 5));
      setRecentAssignments((assignmentRes.data || []).slice(0, 5));
      setUpcomingClasses((classesRes.data || []).slice(0, 3));
      setQuizzes((quizzesRes.data || []).slice(0, 3));
    } catch (error) {
      console.error('Error fetching LMS dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Content', 
      value: stats.total_content || 0, 
      icon: BookOpen, 
      color: 'bg-blue-500',
      path: '/lms/content'
    },
    { 
      title: 'Assignments', 
      value: stats.total_assignments || 0, 
      icon: ClipboardList, 
      color: 'bg-green-500',
      path: '/lms/assignments'
    },
    { 
      title: 'Pending Review', 
      value: stats.pending_submissions || 0, 
      icon: FileText, 
      color: 'bg-orange-500',
      path: '/lms/submissions'
    },
    { 
      title: 'Announcements', 
      value: stats.total_announcements || 0, 
      icon: Bell, 
      color: 'bg-purple-500',
      path: '/lms/announcements'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lms-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="text-blue-600" size={32} />
            Learning Management System
          </h1>
          <p className="text-gray-600 mt-1">Manage course content, assignments, and track student progress</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(stat.path)}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-full`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate('/lms/content/add')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-content-btn"
            >
              <BookOpen size={18} className="mr-2" />
              Add Content
            </Button>
            <Button 
              onClick={() => navigate('/lms/assignments/add')}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-assignment-btn"
            >
              <ClipboardList size={18} className="mr-2" />
              Create Assignment
            </Button>
            <Button 
              onClick={() => navigate('/lms/announcements/add')}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-announcement-btn"
            >
              <Bell size={18} className="mr-2" />
              Post Announcement
            </Button>
            <Button 
              onClick={() => navigate('/lms/online-classes')}
              className="bg-red-600 hover:bg-red-700"
              data-testid="schedule-class-btn"
            >
              <Video size={18} className="mr-2" />
              Online Classes
            </Button>
            <Button 
              onClick={() => navigate('/lms/quizzes')}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="quizzes-btn"
            >
              <FileQuestion size={18} className="mr-2" />
              Quizzes & Tests
            </Button>
            <Button 
              onClick={() => navigate('/lms/discussions')}
              variant="outline"
              data-testid="discussions-btn"
            >
              <MessageCircle size={18} className="mr-2" />
              Discussion Forum
            </Button>
            <Button 
              onClick={() => navigate('/lms/submissions')}
              variant="outline"
              data-testid="view-submissions-btn"
            >
              <FileText size={18} className="mr-2" />
              Review Submissions
            </Button>
            <Button 
              onClick={() => navigate('/lms/analytics')}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="student-analytics-btn"
            >
              <BarChart3 size={18} className="mr-2" />
              Student Analytics
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Content & Assignments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Content */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Content</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/lms/content')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentContent.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No content uploaded yet</p>
            ) : (
              <div className="space-y-3">
                {recentContent.map((content, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded ${content.content_type === 'youtube' ? 'bg-red-100' : 'bg-blue-100'}`}>
                        {content.content_type === 'youtube' ? (
                          <span className="text-red-600 text-sm">▶</span>
                        ) : (
                          <FileText size={16} className="text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{content.title}</p>
                        <p className="text-xs text-gray-500">{content.content_type} • {content.views || 0} views</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Assignments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Assignments</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/lms/assignments')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentAssignments.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No assignments created yet</p>
            ) : (
              <div className="space-y-3">
                {recentAssignments.map((assignment, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-green-100">
                        <ClipboardList size={16} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{assignment.title}</p>
                        <p className="text-xs text-gray-500">
                          Due: {formatDate(assignment.due_date)} • {assignment.submission_count || 0} submissions
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      new Date(assignment.due_date) < new Date() ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {new Date(assignment.due_date) < new Date() ? 'Closed' : 'Active'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LMSDashboard;
